import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, TextField, Typography } from '@mui/material';
import api from '../../services/api';
import { readNokeError } from './restockPlan';
import { newReference } from './merchandisingTypes';
import { useMerchandisingWrite } from './useMerchandisingWrite';

interface Counts { store: number; office: number; transit: number }
interface Snapshot { item: { variant_id: number; sku: string; color: string | null; size: string | null }; expected: Counts & { registered: boolean } }
export interface InventoryEditingProps {
  onInventoryUpdated?: () => Promise<boolean>;
  onInventoryEditingChange?: (key: string, editing: boolean) => void;
  inventoryDisabled?: boolean;
  recoveringInventoryIds?: number[];
}
export function pendingInventoryIds(): number[] {
  try {
    return Object.keys(sessionStorage).filter((key) => /^noke-merchandising-write:inventory-count:\d+$/.test(key)).map((key) => {
      const value = JSON.parse(sessionStorage.getItem(key) || 'null');
      const id = Number(key.split(':').pop());
      return value?.path === '/api/noke/inventory/counts' && value.method === 'post' && value.body?.variant_id === id && typeof value.body?.client_ref === 'string' ? id : 0;
    }).filter((id) => id > 0);
  } catch { return []; }
}
const fields = [['store', '門市'], ['office', '辦公室'], ['transit', '在途']] as const;
const valid = (value: string) => /^\d+$/.test(value) && Number.isSafeInteger(Number(value)) && Number(value) <= 2147483647;

const InventoryCountEditor: React.FC<InventoryEditingProps & { variantId: number; label: string; recovery?: boolean; onDismiss?: () => void }> = ({ variantId, label, recovery = false, inventoryDisabled, onInventoryUpdated, onInventoryEditingChange, onDismiss }) => {
  const write = useMerchandisingWrite(`inventory-count:${variantId}`);
  const key = useRef(newReference(`inventory-editor:${variantId}`)).current;
  const [open, setOpen] = useState(recovery || !!write.pending);
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [draft, setDraft] = useState({ store: '', office: '', transit: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [stale, setStale] = useState(false);
  const [saved, setSaved] = useState(false);
  const request = useRef(0);
  useEffect(() => { onInventoryEditingChange?.(key, open || write.locked); return () => onInventoryEditingChange?.(key, false); }, [key, open, write.locked, onInventoryEditingChange]);
  useEffect(() => () => { request.current += 1; }, []);
  const load = async () => {
    const id = ++request.current; setLoading(true); setError('');
    try {
      const { data } = await api.get<Snapshot>(`/api/noke/inventory/${variantId}/counts`);
      if (id !== request.current) return;
      if (data.item.variant_id !== variantId) throw new Error('庫存色碼不符，請重新讀取。');
      setSnapshot(data); setDraft({ store: String(Math.max(0, data.expected.store)), office: String(data.expected.office), transit: String(data.expected.transit) }); setStale(false);
    } catch (err) { if (id === request.current) setError(readNokeError(err, '無法讀取最新庫存。')); }
    finally { if (id === request.current) setLoading(false); }
  };
  const refresh = async () => {
    setLoading(true); setError('');
    try {
      if (!onInventoryUpdated || !await onInventoryUpdated()) { setError('庫存已儲存，補貨建議尚未更新。請重試更新，不必再儲存庫存。'); return; }
      setOpen(false); setSaved(false); setSnapshot(null);
    } catch { setError('庫存已儲存，補貨建議尚未更新。請重試更新，不必再儲存庫存。'); }
    finally { setLoading(false); }
  };
  const save = async () => {
    if (loading || write.busy || stale || saved || (!write.pending && (!snapshot || !fields.every(([field]) => valid(draft[field]))))) return;
    const ok = await write.submit('post', '/api/noke/inventory/counts', { variant_id: variantId, expected: snapshot?.expected,
      counts: { store: Number(draft.store), office: Number(draft.office), transit: Number(draft.transit) } });
    if (ok) { setSaved(true); await refresh(); }
  };
  useEffect(() => { if (write.errorStatus === 409) setStale(true); }, [write.errorStatus]);
  if (!open) return recovery ? null : <Button size="small" disabled={inventoryDisabled} aria-label={`${label} 編輯庫存`} onClick={() => { setOpen(true); setSaved(false); load(); }}>編輯庫存</Button>;
  const frozen = write.pending?.body.counts as Counts | undefined;
  return <Box role="group" aria-label={`${label} 庫存編輯`} sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
    <Typography variant="caption" fontWeight={600}>{label} · 庫存數量</Typography>
    {recovery && <Typography variant="body2">上次儲存結果尚待確認，請重試同一筆。</Typography>}
    {snapshot && !snapshot.expected.registered && <Typography variant="caption" sx={{ display: 'block' }}>尚未登記；按儲存後才建立庫存紀錄。</Typography>}
    {snapshot && snapshot.expected.store < 0 && <Typography variant="caption" sx={{ display: 'block' }}>原門市庫存 {snapshot.expected.store}；請確認實際非負數量。</Typography>}
    {!saved && (snapshot || frozen) && <Box sx={{ display: 'flex', gap: 0.75, mt: 1 }}>
      {fields.map(([field, name]) => <TextField key={field} label={name} type="number" size="small" value={frozen ? frozen[field] : draft[field]} disabled={loading || write.locked || stale} onChange={(event) => setDraft({ ...draft, [field]: event.target.value })}
        inputProps={{ min: 0, max: 2147483647, step: 1 }} error={!frozen && !valid(draft[field])} sx={{ flex: 1 }} />)}
    </Box>}
    {saved && <Alert severity="success" sx={{ mt: 1 }}>庫存已儲存。</Alert>}
    {loading && <Typography role="status" variant="caption">{saved ? '正在重新計算補貨建議…' : '正在讀取最新庫存…'}</Typography>}
    {(error || (!saved && write.error)) && <Alert severity="warning" sx={{ mt: 1 }}>{error || write.error}</Alert>}
    {stale && <Typography variant="body2">庫存已被其他操作更新。重新讀取後再填寫，未覆蓋新數量。</Typography>}
    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
      {saved ? <Button disabled={loading} onClick={refresh}>重試更新補貨建議</Button> : stale || (!snapshot && !write.pending) ? <Button disabled={loading} onClick={load}>重新讀取最新庫存</Button> : <Button variant="contained" size="small" disabled={loading || write.busy || (!write.pending && !fields.every(([field]) => valid(draft[field])))} onClick={save}>{write.pending ? '重試同一筆庫存' : '儲存'}</Button>}
      {!saved && <Button size="small" disabled={write.locked} onClick={() => { if (write.locked) return; request.current += 1; setOpen(false); setSnapshot(null); onDismiss?.(); }}>取消</Button>}
    </Box>
  </Box>;
};
export default InventoryCountEditor;
